
import { computed, defineComponent, onBeforeMount, PropType, reactive, toRaw } from 'vue'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// validation
import useVuelidate from '@vuelidate/core'
import { PermissionValidation } from '@/modules/GatewayModule/validations/Permission.validation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { GatewayMicroserviceT, GatewayPermissionT } from '@/core/types/gateway.types'

// realisation
import { gateway } from '@/core/realisations/gateway.realisation'

export default defineComponent({
    components: {
        modal: Modal,
        'the-input': TheInput,
        'the-select': TheSelect,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isDelete: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<GatewayPermissionT>,
            default: null,
        },
    },
    emits: ['create', 'update', 'delete', 'cancel'],
    setup(props, { emit }) {
        const state = reactive({
            id: null as number | null,
            name: null as string | null,
            description: null as string | null,
            microservice_id: null as number | null,
        })

        const state_form$ = useVuelidate(PermissionValidation, state)

        const data_state = reactive({
            microservices: new DataState<GatewayMicroserviceT>({}),
        })

        const loadMicroservices = async (page: number) => {
            data_state.microservices.pagination.current_page = page

            try {
                data_state.microservices.is_loading = true

                const response = await gateway.getMicroservices(page)
                data_state.microservices.list = response.data
                data_state.microservices.pagination.total_pages = response.meta.last_page

                data_state.microservices.is_failed = false
                data_state.microservices.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.microservices.failure.catchError(error)

                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_failed = true
                }
            } finally {
                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_loading = false
                }
            }
        }

        const loadMoreMicroservices = async (page: number) => {
            if (page > data_state.microservices.pagination.total_pages) return
            data_state.microservices.pagination.current_page = page

            try {
                data_state.microservices.is_loading = true

                const response = await gateway.getMicroservices(page)
                data_state.microservices.list = data_state.microservices.list.concat(response.data)
                data_state.microservices.pagination.total_pages = response.meta.last_page

                data_state.microservices.is_failed = false
                data_state.microservices.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.microservices.failure.catchError(error)

                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_failed = true
                }
            } finally {
                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_loading = false
                }
            }
        }

        const modalTitle = computed(() => {
            if (props.isCreate) return 'Добавление доступа'
            if (props.isUpdate) return 'Редактирование доступа'
            if (props.isDelete) return 'Удаление доступа'
            return 'modal title'
        })

        const modalConfirmButtonText = computed(() => {
            if (props.isCreate) return 'Добавить'
            if (props.isUpdate) return 'Редактировать'
            if (props.isDelete) return 'Удалить'
            return 'Подтвердить'
        })

        const emitCreate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            const payload = {
                name: state.name,
                description: state.description,
                microservice_id: state.microservice_id,
            }

            emit('create', payload)
        }

        const emitUpdate = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            emit('update', toRaw(state))
        }

        const emitDelete = () => {
            emit('delete', state.id)
        }

        const emitManager = () => {
            if (props.isCreate) emitCreate()
            if (props.isUpdate) emitUpdate()
            if (props.isDelete) emitDelete()
        }

        const emitCancel = () => {
            emit('cancel')
        }

        onBeforeMount(() => {
            loadMicroservices(1)
            if (!props.initialValue) return

            state.id = props.initialValue.id
            state.name = props.initialValue.name
            state.description = props.initialValue.description
            state.microservice_id = props.initialValue.microservice_id
        })

        return {
            state,
            state_form$,
            data_state,
            loadMicroservices,
            loadMoreMicroservices,
            modalTitle,
            modalConfirmButtonText,
            emitManager,
            emitCancel,
        }
    },
})
